import {
  IBuilderExpandEvent,
  IBuilderFoldEvent,
  IBuilderMergeEvent,
  IPopulateBuilderPayload,
  IPopulateFunnelBuilder,
  IStep as IParsedStep,
  RowType,
} from '../../../redux/slices/oldBuilder/types';
import {
  builderPopulateCohortAnalysis,
  populateReportData,
} from '../../../redux/slices/oldBuilder';
import {
  parseBreakdownToStatePayload,
  parseGlobalCohortsToStatePayload,
  populateBinningFromBucketDef,
  populateFunnelState,
} from '../../../components/Builder/utils/builderUtils';
import {
  defaultPopulateQueryAnalysisFields,
  defaultReportFilters,
  defaultStep,
  partitionDictionary,
} from '../../../redux/slices/oldBuilder/defaults';
import { isNil } from 'lodash';
import {
  ExpressionModeKey,
  ICompareExpandEventFieldsFragment,
  ICompareMergeEventFieldsFragment,
  IEventConditionFieldsFragment,
  IFormulaTemplateFieldsFragment,
  IPathExpandEventFieldsFragment,
  IPathFoldEventFieldsFragment,
  IPathMergeEventFieldsFragment,
  IPopulateBuilderAnalysisFieldsFragment,
  QueryType,
} from '../../apollo/graphqlSchema';
import { assertCondition } from '../../types';
import { AppDispatch } from '../../../redux/store';
import { IAnalysisResponse, IFunnelAnalysis } from '../../apollo/responseTypes';
import { MaybeNil } from '../../interfaces';
import { parseExpressionMeasureToBuilder } from '../../builder/measures/parseExpressionMeasureToBuilder';
import { parseCohortRefToBuilder } from '../../builder/cohorts';
import { parseDataTableMeasureToBuilder } from '../../builder/measures/parseDataTableMeasureToBuilder';
import parseCriteriaFields from '../../builder/significance/parseCriteriaFields';
import parseMetricFields from '../../builder/significance/parseMetricFields';

export const parseExpandEvent = ({
  eventCondition,
  field,
}:
  | IPathExpandEventFieldsFragment
  | ICompareExpandEventFieldsFragment): IBuilderExpandEvent => {
  return {
    isActive: true,
    eventCondition,
    field,
  };
};

export const parseMergeEvent = ({
  eventConditions,
  isNameAutoGenerated,
  name,
}:
  | IPathMergeEventFieldsFragment
  | ICompareMergeEventFieldsFragment): IBuilderMergeEvent => {
  const alias = !isNameAutoGenerated && name ? name : null;

  return {
    isActive: true,
    name: alias,
    isNameAutoGenerated,
    eventConditions,
  };
};

const parseFoldEvent = ({
  eventCondition,
}: IPathFoldEventFieldsFragment): IBuilderFoldEvent => {
  return {
    isActive: true,
    eventCondition,
  };
};

const onQueryAnalysisInfoFetch: QueryCallback = (
  dispatch,
  analysisBuilderInfo,
) => {
  assertCondition(
    analysisBuilderInfo.__typename === 'Expression' ||
      analysisBuilderInfo.__typename === 'ExpressionTemplate',
  );
  const sourceId =
    analysisBuilderInfo.__typename === 'Expression'
      ? analysisBuilderInfo.id
      : null;
  const { end, start, timeUnit, comparisonConfig, query, datePicker } =
    analysisBuilderInfo;

  const {
    breakdown,
    filter,
    dateKind,
    schema,
    timeWindow,
    subject,
    subjectBucketDef,
    triggerCondition,
    triggerSubject,
    name: expressionName,
    isNameAutoGenerated: isExpressionNameAutoGenerated,
    cohortRefs,
    compoundMeasureUserAlias,
    measure,
    measureProperties,
    samplePercentage,
    expressionSettings,
    expressionMode,
    includeMissingDates,
    llmChatMessages,
  } = query;

  const { builderBreakdown, breakdownCohorts } = parseBreakdownToStatePayload(
    breakdown,
    QueryType.Expression,
  );

  // Prompt is visible on loading a template or a report, only if it was created with LLM
  // (i.e., has previous messages). This is always the case for "in-progress" conversations.
  const isPromptVisible = llmChatMessages.length > 0;

  const populateBuilderPayload: IPopulateBuilderPayload = {
    ...defaultPopulateQueryAnalysisFields,
    activeSchemaId: schema.id,
    timeWindow,
    sourceId,
    breakdownCohorts,
    selectedOptions: {
      ...defaultPopulateQueryAnalysisFields.selectedOptions,
      mode: expressionMode.key,
      timeUnit,
      includeMissingDates: includeMissingDates ?? false,
    },
    breakdown: builderBreakdown,
    globalCohorts: parseGlobalCohortsToStatePayload(cohortRefs),
    additionalSettings: expressionSettings,
    comparisonConfig,
    dateInput: {
      startDate: start,
      endDate: end,
      datePicker,
    },
    binning: null,
    llmContext: {
      isPromptVisible,
      previousMessages: llmChatMessages,
    },
  };

  if (samplePercentage) {
    populateBuilderPayload.formulaSamplingRate = samplePercentage;
  }

  if (filter) {
    populateBuilderPayload.filter = filter;
  }

  if (compoundMeasureUserAlias?.aliasId) {
    populateBuilderPayload.compoundMeasureUserAliasId =
      compoundMeasureUserAlias.aliasId;
  }

  // Handle Impact analysis mode fields
  if (triggerSubject && triggerCondition) {
    populateBuilderPayload.selectedOptions.triggerSubject = triggerSubject.key;
    populateBuilderPayload.trigger = triggerCondition;

    if (expressionMode.key !== ExpressionModeKey.Impact) {
      populateBuilderPayload.selectedOptions.mode = ExpressionModeKey.Impact;
    }
  }

  if (subject && subjectBucketDef) {
    populateBuilderPayload.selectedOptions.subject = subject.key;

    populateBuilderPayload.binning =
      populateBinningFromBucketDef(subjectBucketDef);

    if (expressionMode.key !== ExpressionModeKey.Histogram) {
      populateBuilderPayload.selectedOptions.mode = ExpressionModeKey.Histogram;
    }
  }

  if (dateKind?.key) {
    populateBuilderPayload.selectedOptions.dateKind = dateKind.key;
  }

  const { measures, resultFilter, measureFormula } =
    parseExpressionMeasureToBuilder(measure, measureProperties);

  populateBuilderPayload.measures = measures;
  populateBuilderPayload.resultFilter = resultFilter;
  if (measureFormula?.expression) {
    populateBuilderPayload.measureFormula = measureFormula;
  }

  if (measure.__typename === 'CompoundMeasure' && measure?.version?.versionId) {
    populateBuilderPayload.compoundMeasureName = measure.name;
    populateBuilderPayload.compoundMeasureId = measure.version.versionId;
  }

  if (!isExpressionNameAutoGenerated && !isNil(expressionName)) {
    populateBuilderPayload.alias = expressionName;
  }

  dispatch(populateReportData(populateBuilderPayload));
};

export const createFunnelBuilderStateFromAnalysis = (
  analysis: { id: MaybeNil<string> } & Pick<
    IFunnelAnalysis,
    'end' | 'query' | 'start' | 'timeUnit' | 'datePicker'
  >,
): IPopulateFunnelBuilder => {
  const { id: sourceId, end, start, timeUnit, query, datePicker } = analysis;

  const {
    breakdown,
    filter,
    steps,
    stepOrders,
    negativeSteps,
    dateKind,
    schema,
    partitions,
    subject,
    order,
    timeWindow,
    cohortRefs,
    groupby,
    mode,
    samplePercentage,
    funnelSettings,
    llmChatMessages,
  } = query;

  const queryType = QueryType.Funnel;

  const parsedSteps: IParsedStep[] = steps.map((step, stepIndex) => {
    const { name, stepConditions } = step;

    const currNegativeStep = negativeSteps.filter(
      (payloadStep) => payloadStep.seq === stepIndex,
    );

    // parse negative event conditions
    const parsedNegativeSteps: IEventConditionFieldsFragment[] = [];
    for (const step of currNegativeStep) {
      const { stepConditions } = step;
      for (const eventCondition of stepConditions) {
        parsedNegativeSteps.push(eventCondition);
      }
    }

    if (step.conversionWindow) {
      return {
        name,
        eventConditions: stepConditions,
        negativeSteps: parsedNegativeSteps,
        userAliasId: step.userAlias.aliasId,
        // kobol doesn't return partition part in return. therefore declare below
        partitionValue: {
          __typename: 'PartitionValue',
          partition: partitionDictionary.ConversionWindow,
          conversionWindow: step.conversionWindow,
        },
        showPartition: true,
      };
    }

    return {
      ...defaultStep(),
      name,
      eventConditions: stepConditions,
      negativeSteps: parsedNegativeSteps,
      userAliasId: step.userAlias.aliasId,
    };
  });

  const { builderBreakdown, breakdownCohorts } = parseBreakdownToStatePayload(
    breakdown,
    queryType,
  );

  // Prompt is visible on loading a template or a report, only if it was created with LLM
  // (i.e., has previous messages). This is always the case for "in-progress" conversations.
  const isPromptVisible = llmChatMessages.length > 0;

  const populateBuilderPayload: IPopulateFunnelBuilder = {
    ...defaultReportFilters,
    queryType,
    steps: parsedSteps,
    stepOrders: stepOrders.map((stepOrder) => stepOrder.key),
    activeSchemaId: schema.id,
    timeWindow,
    selectedOptions: {
      type: queryType,
      subject: subject?.key,
      partitionValues: partitions,
      funnelOrder: order.key,
      dateKind: dateKind?.key,
      groupby: groupby?.key,
      mode: mode?.key,
      funnelCustomOrder: order.key,
      timeUnit,
    },
    sourceId,
    breakdownCohorts,
    globalCohorts: parseGlobalCohortsToStatePayload(cohortRefs),
    additionalSettings: funnelSettings,
    breakdown: builderBreakdown,
    dateInput: {
      startDate: start,
      endDate: end,
      datePicker,
    },
    llmContext: {
      isPromptVisible,
      previousMessages: llmChatMessages,
    },
  };

  if (samplePercentage) {
    populateBuilderPayload.formulaSamplingRate = samplePercentage;
  }

  if (filter) {
    populateBuilderPayload.filter = filter;
  }

  return populateBuilderPayload;
};

const onFunnelAnalysisInfoFetch: QueryCallback = (
  dispatch,
  analysisBuilderInfo,
) => {
  assertCondition(
    analysisBuilderInfo.__typename === 'Funnel' ||
      analysisBuilderInfo.__typename === 'FunnelTemplate',
  );
  const builderInfoWithId =
    analysisBuilderInfo.__typename === 'FunnelTemplate'
      ? { id: null, ...analysisBuilderInfo }
      : analysisBuilderInfo;
  const funnelBuilderState =
    createFunnelBuilderStateFromAnalysis(builderInfoWithId);
  populateFunnelState(dispatch, funnelBuilderState);
};

const onPathAnalysisInfoFetch: QueryCallback = (
  dispatch,
  analysisBuilderInfo,
) => {
  assertCondition(
    analysisBuilderInfo.__typename === 'Path' ||
      analysisBuilderInfo.__typename === 'PathTemplate',
  );
  const sourceId =
    analysisBuilderInfo.__typename === 'Path' ? analysisBuilderInfo.id : null;
  const { start, end, query, datePicker } = analysisBuilderInfo;

  const {
    filter,
    breakdown,
    startConditions,
    endConditions,
    direction,
    schema,
    partitions,
    subject,
    cohortRefs,
    groupby,
    mergeEvents,
    expandEvents,
    foldEvents,
    samplePercentage,
    // TODO: path mode has been aliased because clash with Funnel's mode, redesign the type system
    pathMode,
    pathSettings,
    llmChatMessages,
  } = query;

  const queryType = QueryType.Path;

  const parsedMergeEvents = mergeEvents.map(parseMergeEvent);
  const parsedExpandEvents = expandEvents.map(parseExpandEvent);
  const parsedFoldEvents = foldEvents.map(parseFoldEvent);

  const { builderBreakdown, breakdownCohorts } = parseBreakdownToStatePayload(
    breakdown,
    queryType,
  );

  const populateBuilderPayload: IPopulateBuilderPayload = {
    ...defaultReportFilters,
    queryType,
    paths: [startConditions, endConditions],
    direction: direction.key,
    activeSchemaId: schema.id,
    mergeEvents: parsedMergeEvents,
    expandEvents: parsedExpandEvents,
    foldEvents: parsedFoldEvents,
    sourceId,
    breakdownCohorts,
    selectedOptions: {
      type: queryType,
      partitionValues: partitions,
      mode: pathMode.key,
    },
    breakdown: builderBreakdown,
    globalCohorts: parseGlobalCohortsToStatePayload(cohortRefs),
    additionalSettings: pathSettings,
    dateInput: {
      startDate: start,
      endDate: end,
      datePicker,
    },
    llmContext: {
      isPromptVisible: llmChatMessages.length > 0,
      previousMessages: llmChatMessages,
    },
  };

  if (samplePercentage) {
    populateBuilderPayload.formulaSamplingRate = samplePercentage;
  }

  if (filter) {
    populateBuilderPayload.filter = filter;
  }

  if (subject?.key) {
    populateBuilderPayload.selectedOptions.subject = subject.key;
  }

  if (groupby?.key) {
    populateBuilderPayload.selectedOptions.groupby = groupby.key;
  }

  dispatch(populateReportData(populateBuilderPayload));
};

const onRetentionAnalysisInfoFetch: QueryCallback = (
  dispatch,
  analysisBuilderInfo,
) => {
  assertCondition(
    analysisBuilderInfo.__typename === 'Retention' ||
      analysisBuilderInfo.__typename === 'RetentionTemplate',
  );
  const sourceId =
    analysisBuilderInfo.__typename === 'Retention'
      ? analysisBuilderInfo.id
      : null;
  const { start, end, query, datePicker } = analysisBuilderInfo;

  const {
    breakdown,
    filter,
    startConditions,
    returnConditions,
    schema,
    cohortUnit,
    retentionUnit,
    timeWindow,
    subject,
    cohortRefs,
    attribution,
    groupby,
    calculation,
    calculationWindow,
    customTimeBrackets,
    view,
    samplePercentage,
    retentionSettings,
    llmChatMessages,
  } = query;

  const queryType = QueryType.Retention;
  const { builderBreakdown, breakdownCohorts } = parseBreakdownToStatePayload(
    breakdown,
    queryType,
  );

  const populateBuilderPayload: IPopulateBuilderPayload = {
    ...defaultReportFilters,
    queryType,
    retention: {
      retentionConditions: [startConditions, returnConditions],
      activeRetentionUnit: retentionUnit,
      customTimeBrackets,
    },
    activeSchemaId: schema.id,
    timeWindow,
    selectedOptions: {
      type: queryType,
      subject: subject?.key,
      attribution: attribution?.key,
      groupby: groupby?.key,
      calculation: calculation.key,
      calculationWindow: calculationWindow.key,
      retentionView: view.key,
      cohortUnit,
    },
    sourceId,
    breakdownCohorts,
    breakdown: builderBreakdown,
    globalCohorts: parseGlobalCohortsToStatePayload(cohortRefs),
    additionalSettings: retentionSettings,
    dateInput: {
      startDate: start,
      endDate: end,
      datePicker,
    },
    llmContext: {
      isPromptVisible: llmChatMessages.length > 0,
      previousMessages: llmChatMessages,
    },
  };

  if (samplePercentage) {
    populateBuilderPayload.formulaSamplingRate = samplePercentage;
  }

  if (filter) {
    populateBuilderPayload.filter = filter;
  }

  dispatch(populateReportData(populateBuilderPayload));
};

const onPredictionAnalysisInfoFetch: QueryCallback = (dispatch, analysis) => {
  assertCondition(analysis.__typename === 'Prediction');
  const { id: sourceId, start, end, datePicker } = analysis;

  const query = analysis.query;

  const { schema, sourceCohortRef, targetCohortRef, eventCondition, window } =
    query;

  const populateBuilderPayload: IPopulateBuilderPayload = {
    queryType: QueryType.Prediction,
    prediction: {
      timeWindow: window,
      eventCondition,
    },
    activeSchemaId: schema.id,
    sourceId,
    [RowType.predictionCohorts]: [sourceCohortRef, targetCohortRef].map(
      parseCohortRefToBuilder,
    ),
    dateInput: {
      startDate: start,
      endDate: end,
      datePicker,
    },
  };

  dispatch(populateReportData(populateBuilderPayload));
};

const onDataTableAnalysisInfoFetch: QueryCallback = (
  dispatch,
  analysisBuilderInfo,
) => {
  assertCondition(
    analysisBuilderInfo.__typename === 'DataTable' ||
      analysisBuilderInfo.__typename === 'DataTableTemplate',
  );
  const sourceId =
    analysisBuilderInfo.__typename === 'DataTable'
      ? analysisBuilderInfo.id
      : null;
  const { end, start, query, comparisonConfig, datePicker } =
    analysisBuilderInfo;

  const {
    breakdown,
    filter,
    dateKind: payloadSelectedDateKind,
    schema,
    timeWindow,
    cohortRefs,
    measures,
    measuresProperties,
    samplePercentage,
    dataTableSettings,
    llmChatMessages,
  } = query;

  const queryType = QueryType.DataTable;

  const { builderBreakdown, breakdownCohorts } = parseBreakdownToStatePayload(
    breakdown,
    queryType,
  );

  const populateBuilderPayload: IPopulateBuilderPayload = {
    ...defaultReportFilters,
    queryType,
    activeSchemaId: schema.id,
    timeWindow,
    sourceId,
    breakdownCohorts,
    selectedOptions: {
      type: queryType,
    },
    breakdown: builderBreakdown,
    globalCohorts: parseGlobalCohortsToStatePayload(cohortRefs),
    additionalSettings: dataTableSettings,
    comparisonConfig,
    dateInput: {
      startDate: start,
      endDate: end,
      datePicker,
    },
    llmContext: {
      isPromptVisible: llmChatMessages.length > 0,
      previousMessages: llmChatMessages,
    },
  };

  if (samplePercentage) {
    populateBuilderPayload.formulaSamplingRate = samplePercentage;
  }

  if (filter) {
    populateBuilderPayload.filter = filter;
  }

  if (payloadSelectedDateKind?.key) {
    populateBuilderPayload.selectedOptions.dateKind =
      payloadSelectedDateKind.key;
  }

  populateBuilderPayload.dataTableMeasures = parseDataTableMeasureToBuilder(
    measures,
    measuresProperties,
  );

  dispatch(populateReportData(populateBuilderPayload));
};

const onSignificanceAnalysisInfoFetch: QueryCallback = (
  dispatch,
  analysisBuilderInfo,
) => {
  assertCondition(
    analysisBuilderInfo.__typename === 'Significance' ||
      analysisBuilderInfo.__typename === 'SignificanceTemplate',
  );
  const sourceId =
    analysisBuilderInfo.__typename === 'Significance'
      ? analysisBuilderInfo.id
      : null;

  const { end, start, query, datePicker } = analysisBuilderInfo;
  const {
    cohortRefs,
    filter,
    schema,
    confidenceLevel,
    significanceLevel,
    metricType,
    significanceMode,
    significanceSubject,
    test,
    criteria,
    metric,
    llmChatMessages,
  } = query;

  const queryType = QueryType.Significance;

  const populateBuilderPayload: IPopulateBuilderPayload = {
    queryType,
    activeSchemaId: schema.id,
    sourceId,
    filter: filter || undefined,
    globalCohorts: parseGlobalCohortsToStatePayload(cohortRefs),
    significance: {
      criteria: parseCriteriaFields(criteria),
      metric: parseMetricFields(metric),
      confidenceLevel,
      significanceLevel,
      errors: {},
    },
    selectedOptions: {
      type: queryType,
      metric: metricType.key,
      mode: significanceMode.key,
      subject: significanceSubject.key,
      test: test.key,
    },
    dateInput: {
      startDate: start,
      endDate: end,
      datePicker,
    },
    llmContext: {
      isPromptVisible: llmChatMessages.length > 0,
      previousMessages: llmChatMessages,
    },
  };

  dispatch(populateReportData(populateBuilderPayload));
};

const onCohortSampleAnalysisInfoFetch: QueryCallback = (
  dispatch,
  analysisBuilderInfo,
) => {
  assertCondition(
    analysisBuilderInfo.__typename === 'CohortSample' ||
      analysisBuilderInfo.__typename === 'CohortSampleTemplate',
  );
  const { start, end, query, datePicker } = analysisBuilderInfo;
  const {
    cohort,
    schema,
    operation,
    samplePercentage,
    sampleAbsolute,
    compare,
    dateRange,
    llmChatMessages,
  } = query;
  const schemaId = schema.id;

  // Prompt is visible on loading a template or a report, only if it was created with LLM
  // (i.e., has previous messages). This is always the case for "in-progress" conversations.
  const isPromptVisible = llmChatMessages.length > 0;

  dispatch(
    builderPopulateCohortAnalysis({
      activeSchemaId: schemaId,
      cohort,
      operation,
      compare,
      samplePercentage,
      sampleAbsolute,
      lockedDateRange: dateRange,
      dateInput: {
        startDate: start,
        endDate: end,
        datePicker,
      },
      isPromptVisible,
      llmChatMessages,
    }),
  );
};

type QueryCallback = (
  dispatch: AppDispatch,
  analysisBuilderInfo:
    | IAnalysisResponse
    | IPopulateBuilderAnalysisFieldsFragment
    | IFormulaTemplateFieldsFragment,
) => void;

export const getAnalysisInfoFetchByType: Record<QueryType, QueryCallback> = {
  [QueryType.Expression]: onQueryAnalysisInfoFetch,
  [QueryType.Funnel]: onFunnelAnalysisInfoFetch,
  [QueryType.Retention]: onRetentionAnalysisInfoFetch,
  [QueryType.Prediction]: onPredictionAnalysisInfoFetch,
  [QueryType.Path]: onPathAnalysisInfoFetch,
  [QueryType.DataTable]: onDataTableAnalysisInfoFetch,
  [QueryType.Significance]: onSignificanceAnalysisInfoFetch,
  [QueryType.CohortSample]: onCohortSampleAnalysisInfoFetch,
};
